import React, { useState, useEffect } from "react";
import { Table, Modal, Button, notification, Input, Form, Select } from "antd";
import axios from "axios";

const { TextArea } = Input;
const { Option } = Select;

const CommentairesManager = () => {
  const [form] = Form.useForm();
  const [commentaires, setCommentaires] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewingCommentaire, setViewingCommentaire] = useState(null);
  const [editingCommentaire, setEditingCommentaire] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteCommentaire, setDeleteCommentaire] = useState(null);
  const [loading, setLoading] = useState(false);

  // Types d'entités pour les commentaires (selon votre base de données)
  const entityTypes = [
    { id: "publication", name: "Publication", idField: "publication_id" },
    { id: "projet", name: "Projet", idField: "projet_id" }
  ];

  // État pour filtrer par type d'entité
  const [filterEntityType, setFilterEntityType] = useState("publication");
  const [filterEntityId, setFilterEntityId] = useState("all");

  // Style personnalisé pour les boutons verts
  const greenButtonStyle = {
    backgroundColor: "#4caf50", // Couleur verte
    borderColor: "#4caf50",
    color: "white",
  };

  const openNotification = (type, message) => {
    notification[type]({
      message: message,
    });
  };

  const truncateText = (text, wordLimit) => {
    if (!text) return "";

    const words = text.split(" ");
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(" ") + "...";
    }
    return text;
  };

  const fetchCommentaires = async () => {
    try {
      setLoading(true);
      // Endpoint pour récupérer tous les commentaires
      // Vous aurez besoin d'adapter cet endpoint selon votre API
      let endpoint = "/commentaires";

      // Ajouter les filtres si nécessaire
      if (filterEntityType === "publication" && filterEntityId !== "all") {
        endpoint = `/commentaires/publication/${filterEntityId}`;
      } else if (filterEntityType === "projet" && filterEntityId !== "all") {
        endpoint = `/commentaires/projet/${filterEntityId}`;
      }

      const response = await axios.get(`${process.env.REACT_APP_API_URL}${endpoint}`);

      setCommentaires(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des commentaires:", error);
      openNotification("error", "Erreur lors de la récupération des commentaires.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommentaires();
  }, [filterEntityType, filterEntityId]);

  const handleFilterChange = (type) => {
    setFilterEntityType(type);
  };

  const handleAddCommentaire = async (values) => {
    if (!values.message || !values.entityId || !values.typeEntity) {
      openNotification("error", "Veuillez remplir tous les champs obligatoires.");
      return;
    }

    try {
      setLoading(true);

      // Construire l'objet commentaire selon le schéma de votre base de données
      const commentaireData = {
        message: values.message,
        status: true, // par défaut actif
        utilisateur_id: values.utilisateurId || 1 // Valeur par défaut si non spécifiée
      };

      // Ajouter le champ correspondant au type d'entité
      if (values.typeEntity === "publication") {
        commentaireData.publication_id = values.entityId;
      } else if (values.typeEntity === "projet") {
        commentaireData.projet_id = values.entityId;
      }

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/commentaires`, commentaireData, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });

      openNotification("success", "Commentaire ajouté avec succès.");
      fetchCommentaires();
      setIsAddModalVisible(false);
      form.resetFields();
    } catch (error) {
      openNotification("error", "Erreur lors de l'ajout du commentaire.");
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCommentaire = async (values) => {
    if (!editingCommentaire) {
      openNotification("error", "Aucun commentaire sélectionné pour la modification.");
      return;
    }

    if (!values.message) {
      openNotification("error", "Le message est obligatoire.");
      return;
    }

    try {
      setLoading(true);

      // Préparer les données à mettre à jour, correspondant à votre schéma
      const updatedData = {
        idcomment: editingCommentaire.idcomment,
        message: values.message,
        status: editingCommentaire.status
        // Autres champs à conserver tels quels
      };

      await axios.put(`${process.env.REACT_APP_API_URL}/commentaires/${editingCommentaire.idcomment}`, updatedData, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });

      openNotification("success", "Commentaire modifié avec succès.");
      setIsEditModalVisible(false);
      fetchCommentaires();
      form.resetFields();
    } catch (error) {
      console.error("Erreur lors de la modification:", error);
      openNotification("error", "Erreur lors de la modification du commentaire.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (commentaire) => {
    try {
      setLoading(true);

      // Mettre à jour le statut (inverse de l'état actuel)
      const updatedStatus = commentaire.status === 1 ? 0 : 1;

      await axios.put(`${process.env.REACT_APP_API_URL}/commentaires/${commentaire.idcomment}/status`,
        { status: updatedStatus },
        {
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      openNotification(
        "success",
        `Commentaire ${commentaire.status === 1 ? "désactivé" : "activé"} avec succès.`
      );

      fetchCommentaires();
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
      openNotification("error", "Erreur lors du changement de statut du commentaire.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCommentaire = async (id) => {
    try {
      setLoading(true);

      await axios.delete(`${process.env.REACT_APP_API_URL}/commentaires/${id}`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });

      setCommentaires(commentaires.filter(item => item.id !== id));
      setDeleteModalVisible(false);
      setDeleteCommentaire(null);
      openNotification("success", "Commentaire supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      openNotification("error", "Erreur lors de la suppression du commentaire.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setIsEditModalVisible(false);
    setIsAddModalVisible(false);
    setIsViewModalVisible(false);
    setEditingCommentaire(null);
  };

  const handleEdit = (commentaire) => {
    setEditingCommentaire(commentaire);
    form.setFieldsValue({
      message: commentaire.message
    });
    setIsEditModalVisible(true);
  };

  const handleView = (commentaire) => {
    setViewingCommentaire(commentaire);
    setIsViewModalVisible(true);
  };

  const handleDeleteConfirm = (commentaire) => {
    setDeleteCommentaire(commentaire);
    setDeleteModalVisible(true);
  };

  const filteredCommentaires = commentaires.filter((commentaire) => {
    const searchableText =
      (commentaire.message || '') +
      (commentaire.utilisateur?.nom || '') +
      (commentaire.utilisateur?.prenom || '');
    return searchableText.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getEntityTypeLabel = (commentaire) => {
    if (commentaire.publication) return "Publication";
    if (commentaire.projet) return "Projet";
    return "Non spécifié";
  };

  const getEntityId = (commentaire) => {
    if (commentaire.publication) return commentaire.publication.id;
    if (commentaire.projet) return commentaire.projet.id;
    return "N/A";
  };

  const columns = [
    {
      title: "Type d'entité",
      key: "typeEntity",
      render: (_, record) => <span>{getEntityTypeLabel(record)}</span>,
    },
    {
      title: "ID Entité",
      key: "entityId",
      render: (_, record) => <span>{getEntityId(record)}</span>,
    },
    {
      title: "Auteur",
      key: "auteur",
      render: (_, record) => (
        <span>
          {record.utilisateur
            ? `${record.utilisateur.prenom || ''} ${record.utilisateur.nom || ''}`
            : "Non spécifié"}
        </span>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      render: (text) => truncateText(text, 10),
    },
    {
      title: "Date de création",
      dataIndex: "created",
      key: "created",
      render: (date) => date ? new Date(date).toLocaleDateString("fr-FR") : '-',
    },
    {
      title: "Statut",
      key: "statut",
      render: (_, record) => (
        <span className={record.statut ? "text-success" : "text-danger"}>
          {record.statut ? "Actif" : "Inactif"}
        </span>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      render: (_, record) => (
        <div className="text-end">
          <Button
            className="ms-2 btn btn-sm"
            style={greenButtonStyle}
            onClick={() => handleView(record)}
          >
            Voir
          </Button>
          <Button
            className="ms-2 btn btn-sm"
            style={greenButtonStyle}
            onClick={() => handleToggleStatus(record)}
          >
            {record.statut ? "Désactiver" : "Activer"}
          </Button>
          <Button
            className="ms-2 btn btn-sm"
            style={greenButtonStyle}
            onClick={() => handleEdit(record)}
          >
            Modifier
          </Button>
          <Button
            className="ms-2 btn btn-sm"
            style={greenButtonStyle}
            onClick={() => handleDeleteConfirm(record)}
          >
            Supprimer
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col-sm-7 col-auto">
                <h3 className="page-title">Gestion des Commentaires</h3>
                <div className="mt-3">
                  <Input
                    className="form-control"
                    placeholder="Rechercher un commentaire"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    allowClear
                  />
                </div>
              </div>
              <div className="col-sm-5 col">
                <Button
                  style={greenButtonStyle}
                  onClick={() => {
                    resetForm();
                    setIsAddModalVisible(true);
                  }}
                  className="float-end mt-2"
                >
                  Ajouter un commentaire
                </Button>
              </div>
            </div>
          </div>

          {/* Filtres pour les types d'entités */}
          <div className="row mb-3">
            <div className="col-md-6">
              <Form layout="inline">
                <Form.Item label="Type d'entité">
                  <Select
                    value={filterEntityType}
                    onChange={handleFilterChange}
                    style={{ width: 150 }}
                  >
                    {entityTypes.map(type => (
                      <Option key={type.id} value={type.id}>{type.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="ID de l'entité">
                  <Input
                    placeholder="all pour tous"
                    value={filterEntityId}
                    onChange={(e) => setFilterEntityId(e.target.value || "all")}
                    style={{ width: 150 }}
                  />
                </Form.Item>
                <Form.Item>
                  <Button style={greenButtonStyle} onClick={fetchCommentaires}>
                    Filtrer
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>

          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={filteredCommentaires}
            pagination={{ pageSize: 10 }}
          />

          {/* Modal d'ajout de commentaire */}
          <Modal
            title="Ajouter un commentaire"
            visible={isAddModalVisible}
            onCancel={() => {
              resetForm();
              setIsAddModalVisible(false);
            }}
            footer={null}
            width={800}
          >
            <Form form={form} layout="vertical" onFinish={handleAddCommentaire}>
              <Form.Item
                name="typeEntity"
                label="Type d'entité"
                rules={[{ required: true, message: "Veuillez sélectionner un type d'entité" }]}
                initialValue="publication"
              >
                <Select placeholder="Sélectionnez le type d'entité">
                  {entityTypes.map((type) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="entityId"
                label="ID de l'entité"
                rules={[{ required: true, message: "Veuillez entrer l'ID de l'entité" }]}
              >
                <Input type="number" placeholder="Ex: 1" />
              </Form.Item>

              <Form.Item
                name="utilisateurId"
                label="ID de l'utilisateur"
                rules={[{ required: true, message: "Veuillez entrer l'ID de l'utilisateur" }]}
                initialValue="1"
              >
                <Input type="number" placeholder="Ex: 1" />
              </Form.Item>

              <Form.Item
                name="message"
                label="Message"
                rules={[{ required: true, message: "Veuillez entrer un message" }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Saisissez votre commentaire ici"
                />
              </Form.Item>

              <Form.Item>
                <Button style={greenButtonStyle} htmlType="submit" loading={loading}>
                  Créer
                </Button>
                <Button
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    resetForm();
                    setIsAddModalVisible(false);
                  }}
                >
                  Annuler
                </Button>
              </Form.Item>
            </Form>
          </Modal>

          {/* Modal de modification de commentaire */}
          <Modal
            title="Modifier un commentaire"
            visible={isEditModalVisible}
            onCancel={() => {
              resetForm();
              setIsEditModalVisible(false);
            }}
            footer={null}
            width={800}
          >
            <Form form={form} layout="vertical" onFinish={handleEditCommentaire}>
              <Form.Item
                name="message"
                label="Message"
                rules={[{ required: true, message: "Veuillez entrer un message" }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Saisissez votre commentaire ici"
                />
              </Form.Item>

              <Form.Item>
                <Button style={greenButtonStyle} htmlType="submit" loading={loading}>
                  Mettre à jour
                </Button>
                <Button
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    resetForm();
                    setIsEditModalVisible(false);
                  }}
                >
                  Annuler
                </Button>
              </Form.Item>
            </Form>
          </Modal>

          {/* Modal de confirmation de suppression */}
          <Modal
            title="Confirmer la suppression"
            visible={deleteModalVisible}
            onCancel={() => setDeleteModalVisible(false)}
            footer={[
              <Button key="back" onClick={() => setDeleteModalVisible(false)}>
                Annuler
              </Button>,
              <Button
                key="submit"
                style={greenButtonStyle}
                loading={loading}
                onClick={() => handleDeleteCommentaire(deleteCommentaire?.id)}
              >
                Supprimer
              </Button>,
            ]}
          >
            <p>
              Êtes-vous sûr de vouloir supprimer ce commentaire ? Cette action est
              irréversible.
            </p>
          </Modal>

          {/* Modal pour afficher les détails */}
          <Modal
            title="Détails du commentaire"
            visible={isViewModalVisible}
            onCancel={() => setIsViewModalVisible(false)}
            footer={[
              <Button key="close" style={greenButtonStyle} onClick={() => setIsViewModalVisible(false)}>
                Fermer
              </Button>,
            ]}
          >
            {viewingCommentaire && (
              <div>
                <p>
                  <strong>Type d'entité:</strong> {getEntityTypeLabel(viewingCommentaire)}
                </p>
                <p>
                  <strong>ID de l'entité:</strong> {getEntityId(viewingCommentaire)}
                </p>
                <p>
                  <strong>ID:</strong> {viewingCommentaire.id}
                </p>
                <p>
                  <strong>Message:</strong> {viewingCommentaire.message}
                </p>
                <p>
                  <strong>Statut:</strong>
                  <span className={viewingCommentaire.statut ? "text-success" : "text-danger"}>
                    {viewingCommentaire.statut ? " Actif" : " Inactif"}
                  </span>
                </p>
                <p>
                  <strong>Date de création:</strong>{" "}
                  {viewingCommentaire.created ? new Date(viewingCommentaire.created).toLocaleString("fr-FR") : '-'}
                </p>
                <p>
                  <strong>Dernière mise à jour:</strong>{" "}
                  {viewingCommentaire.updated ? new Date(viewingCommentaire.updated).toLocaleString("fr-FR") : '-'}
                </p>
                <p>
                  <strong>Auteur:</strong>{" "}
                  {viewingCommentaire.utilisateur
                    ? `${viewingCommentaire.utilisateur.prenom || ''} ${viewingCommentaire.utilisateur.nom || ''}`
                    : "Non spécifié"}
                </p>
              </div>
            )}
          </Modal>
        </div>
      </div>
    </>
  );
};

export default CommentairesManager;
